import { adminPath, isNotFoundError, type AdminClient } from '../core/index.js'
import type { ScoreResult, SimulatorSettings } from './score-simulator/simulatorModel.js'
import type { ClosePeriodRequest, FacilityScoreRun, PeriodClose, ScheduledTask, ScoreBreakdown, ScoreHistory, ScoringRun, WorkerScore } from './types.js'

/** The scoring module's admin calls: a worker's scores of any client, a facility's runs, the simulator and the scheduler's runs. */
export interface ScoringAdminApi {
  workerScoreBreakdown(clientId: string, workerId: string): Promise<ScoreBreakdown>
  /** The current running score, computed by the API on first read. */
  workerScore(clientId: string, workerId: string): Promise<WorkerScore>
  workerScoreHistory(clientId: string, workerId: string): Promise<ScoreHistory>
  /** A past period's score (`YYYY-MM`): its final, or its last running score if it never closed. Null when the period has none. */
  workerScoreForPeriod(clientId: string, workerId: string, period: string): Promise<WorkerScore | null>
  /** Recomputes and stores every worker's running score at the facility. */
  recalculateFacilityScores(clientId: string, facilityId: string): Promise<FacilityScoreRun>
  /** Closes a scoring period: finals, tiers and the new period's dates. Without a period, the last one that has ended. */
  closeFacilityPeriod(clientId: string, facilityId: string, request: ClosePeriodRequest): Promise<PeriodClose>
  /** Runs the scoring engine's calculation on made-up activity and settings. Reads and writes no client data. */
  simulateScore(settings: SimulatorSettings): Promise<ScoreResult>
  /** The scheduler's recent runs, newest first. */
  listScoringRuns(): Promise<ScoringRun[]>
  /** Runs a scheduled task now, across every client; answers when it has finished. */
  runScoringTask(task: ScheduledTask): Promise<ScoringRun>
}

export function createScoringAdminApi(client: AdminClient): ScoringAdminApi {
  return {
    workerScoreBreakdown: (clientId, workerId) => client.call('GET', `${adminPath.worker(clientId, workerId)}/score/breakdown`),
    workerScore: (clientId, workerId) => client.call('GET', `${adminPath.worker(clientId, workerId)}/score`),
    workerScoreHistory: (clientId, workerId) => client.call('GET', `${adminPath.worker(clientId, workerId)}/score/history`),
    workerScoreForPeriod: async (clientId, workerId, period) => {
      try {
        return await client.call<WorkerScore>('GET', `${adminPath.worker(clientId, workerId)}/score?period=${encodeURIComponent(period)}`)
      } catch (error) {
        if (isNotFoundError(error)) {
          return null
        }
        throw error
      }
    },
    recalculateFacilityScores: (clientId, facilityId) => client.call('POST', `${adminPath.facility(clientId, facilityId)}/scores/recalculate`, {}),
    closeFacilityPeriod: (clientId, facilityId, request) => client.call('POST', `${adminPath.facility(clientId, facilityId)}/scores/close`, request),
    simulateScore: (settings) => client.call('POST', '/admin/scoring/simulate', settings),
    listScoringRuns: () => client.call('GET', '/admin/scoring/runs'),
    runScoringTask: (task) => client.call('POST', '/admin/scoring/run', { task }),
  }
}
